import type { TechLogo } from '@/components/icons/tech';

interface MarqueeProps {
  items: TechLogo[];
  /** Seconds for one full cycle. Higher is slower. */
  speed?: number;
  direction?: 'left' | 'right';
  className?: string;
}

/**
 * Infinite technology logo ticker.
 *
 * Driven by a CSS keyframe animation rather than GSAP: a constant linear loop
 * needs no JS timeline, and running it on the compositor keeps the main thread
 * free. A GSAP-driven version of this was a measurable source of jank, since it
 * ticked every frame for the entire time the strip was on screen.
 *
 * The track is duplicated and translated by exactly -50%, so the second copy
 * lands where the first began and the seam is invisible.
 */
export function Marquee({
  items,
  speed = 48,
  direction = 'left',
  className = '',
}: MarqueeProps) {
  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{
        maskImage: 'linear-gradient(to right, transparent, black 7%, black 93%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 7%, black 93%, transparent)',
      }}
    >
      <div
        className="flex w-max"
        style={{
          animation: `falcon-marquee ${speed}s linear infinite`,
          animationDirection: direction === 'right' ? 'reverse' : 'normal',
        }}
      >
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0" aria-hidden={copy === 1}>
            {items.map(({ name, Icon }) => (
              <span
                key={`${copy}-${name}`}
                className="flex shrink-0 items-center px-7 sm:px-10"
                title={name}
                /*
                  Logo-only: the name used to sit beside each mark, which made
                  the strip read as a list rather than a logo wall. The title
                  and label keep every mark identifiable without visible text —
                  labelled on the first copy only, since the second is the
                  duplicate that makes the loop seamless and is aria-hidden.
                */
                role={copy === 0 ? 'img' : undefined}
                aria-label={copy === 0 ? name : undefined}
              >
                {/*
                  Fixed HEIGHT, auto width: these marks range from square icons
                  to 4:1 wordmarks, so constraining both axes would letterbox
                  the wide ones inside a square box and leave them tiny.
                */}
                <Icon className="h-12 w-auto max-w-[8rem] shrink-0 sm:h-16 sm:max-w-[11rem]" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
